import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { S2eQuery } from "types";
import {
  formatCurrency,
  formatDateSafe,
  getVoucherUrl,
} from "utils/helpers/accounting";
import { Link } from "react-router-dom";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: S2eQuery = {
  from_date: startOfYear,
  to_date: todayString,
};

const S2eReport: React.FC = () => {
  const { fetchS2eHkd } = useReport();
  const [filters, setFilters] = useState<S2eQuery>(defaultFilters);
  const [submitted, setSubmitted] = useState<S2eQuery | null>(defaultFilters);

  useEffect(() => {
    fetchS2eHkd.mutate(defaultFilters);
  }, [fetchS2eHkd.mutate]);

  const handleFilterChange = useCallback((key: keyof S2eQuery, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchS2eHkd.mutate(filters);
    },
    [fetchS2eHkd, filters],
  );

  // S2e uses the same API as S7
  const responseData = fetchS2eHkd.data?.data;
  const summary = responseData?.summary || {
    opening_balance: 0,
    total_cash_in: 0,
    total_cash_out: 0,
    closing_balance: 0,
    is_balanced: true,
  };
  const transactions = responseData?.transactions || [];

  const columns = useMemo(
    () => [
      {
        headerName: "Chứng từ",
        children: [
          {
            headerName: "Số hiệu",
            field: "voucher_code",
            pinned: "left",
        minWidth: 130,
            cellRenderer: (params: any) => {
              const code =
                (params.data?.voucher_code as string) ??
                (params.data?.code as string) ??
                "";
              const id = params.data?.id;
              const url = getVoucherUrl(code, id);
              if (url && code) {
                return (
                  <Link
                    to={url}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {code}
                  </Link>
                );
              }
              return code;
            },
          },
          {
            headerName: "Ngày, tháng",
            field: "accounting_date",
            pinned: "left",
        minWidth: 130,
            valueFormatter: (params: any) => formatDateSafe(params.value),
          },
        ],
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 260,
        flex: 1,
      },
      {
        headerName: "Số tiền",
        children: [
          {
            headerName: "Thu/Gửi vào",
            field: "cash_in",
            minWidth: 140,
            valueFormatter: (params: any) =>
              params.value !== undefined &&
              params.value !== null &&
              params.value > 0
                ? formatCurrency(Number(params.value))
                : "",
            cellClass: "text-right font-semibold text-green-700",
          },
          {
            headerName: "Chi/Rút ra",
            field: "cash_out",
            minWidth: 140,
            valueFormatter: (params: any) =>
              params.value !== undefined &&
              params.value !== null &&
              params.value > 0
                ? formatCurrency(Number(params.value))
                : "",
            cellClass: "text-right font-semibold text-red-700",
          },
        ],
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      minWidth: 100,
    }),
    [],
  );

  const isLoading = fetchS2eHkd.isPending;

  return (
    <div className="space-y-6">
      {isLoading && (
        <LoadingState
          message="Đang tải báo cáo"
          description="Vui lòng chờ trong giây lát."
        />
      )}

      {/* TT152 Form Header */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="text-sm text-gray-600 space-y-1">
            <p className="font-semibold">HỘ, CÁ NHÂN KINH DOANH: ..........</p>
            <p>Địa chỉ: ............................</p>
            <p>Mã số thuế: ..........................</p>
          </div>
          <div className="text-right text-sm text-gray-500 italic">
            <p className="font-semibold not-italic text-gray-700">
              Mẫu số S2e-HKD
            </p>
            <p>(Kèm theo Thông tư số 152/2025/TT-BTC</p>
            <p>ngày 31 tháng 12 năm 2025 của Bộ trưởng</p>
            <p>Bộ Tài chính)</p>
          </div>
        </div>
        <div className="text-center mb-4">
          <h2 className="text-lg font-bold text-gray-900 uppercase">
            Sổ chi tiết tiền
          </h2>
          <p className="text-sm text-gray-600">
            Kỳ kê khai:{" "}
            {submitted
              ? `${submitted.from_date} → ${submitted.to_date}`
              : "..............."}
          </p>
        </div>
      </div>

      {/* Filter Form */}
      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày *
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày *
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Loại tiền (tùy chọn)
            </label>
            <select
              value={filters.cash_type || ""}
              onChange={(e) =>
                handleFilterChange("cash_type", e.target.value || undefined)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Tất cả</option>
              <option value="CASH">Tiền mặt</option>
              <option value="BANK">Tiền gửi ngân hàng</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="submit"
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
            disabled={isLoading}
          >
            {isLoading ? "Đang tải..." : "Xem báo cáo"}
          </button>
        </div>
      </form>

      {/* Results */}
      {submitted && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
          <p className="text-right text-sm text-gray-600 italic">
            Đơn vị tính: đồng
          </p>

          {/* Summary */}
          <div className="flex flex-wrap items-center gap-6 px-2 py-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-center">
              <p className="text-xs text-gray-600">Số dư đầu kỳ</p>
              <p className="text-base font-bold text-gray-900">
                {formatCurrency(summary.opening_balance)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-600">Tổng thu/gửi vào</p>
              <p className="text-base font-bold text-green-700">
                {formatCurrency(summary.total_cash_in)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-600">Tổng chi/rút ra</p>
              <p className="text-base font-bold text-red-700">
                {formatCurrency(summary.total_cash_out)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-600">Số dư cuối kỳ</p>
              <p className="text-base font-bold text-blue-700">
                {formatCurrency(summary.closing_balance)}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              Kết quả ( {submitted.from_date} → {submitted.to_date} )
            </h2>
            <p className="text-sm text-gray-500">
              {transactions.length} giao dịch
            </p>
          </div>

          <AgGridTable
            rows={transactions}
            columns={columns as any}
            defaultColDef={defaultColDef}
            pagination={false}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default S2eReport;
